/**
 * E-Signature Component for Accountant Portal
 * Enhanced version with better UI and functionality
 */

import React, { useRef, useState, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import axios from 'axios';
import { FileSignature, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Signature {
  id: string;
  report_id: string;
  accountant_id: string;
  accountant_name: string;
  signature_data_url: string;
  signed_at: string;
  version_id?: string;
  notes?: string;
}

interface ESignatureProps {
  reportId: string;
  getAuthHeader: () => any;
  canSign: boolean;
}

export default function ESignature({ reportId, getAuthHeader, canSign }: ESignatureProps) {
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signing, setSigning] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Signature pad
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  
  // Signature method tabs
  const [activeTab, setActiveTab] = useState<'draw' | 'type'>('draw');
  const [typedName, setTypedName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Additional fields (will be populated from profile)
  const [title, setTitle] = useState('Forensic Accountant');
  const [credentials, setCredentials] = useState('');
  const [attestation, setAttestation] = useState(
    'I hereby certify that this report is true and accurate to the best of my knowledge and professional judgment.'
  );
  
  // Profile data
  const [profileData, setProfileData] = useState<any>(null);
  const [accountantName, setAccountantName] = useState('');

  useEffect(() => {
    fetchSignatures();
    fetchAccountantProfile();
  }, [reportId]);

  // Fetch accountant profile to pre-fill credentials
  const fetchAccountantProfile = async () => {
    try {
      // Get basic accountant info from localStorage
      const storedAccountant = localStorage.getItem('accountant_profile');
      if (storedAccountant) {
        const accountant = JSON.parse(storedAccountant);
        setAccountantName(accountant.full_name || '');
        setTypedName(accountant.full_name || '');
      }

      // Try to fetch full profile with credentials
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/accountant-profile/me`,
        { headers: getAuthHeader() }
      );
      
      setProfileData(response.data);
      
      // Build credentials string from profile
      const certs = response.data.certifications || [];
      if (certs.length > 0) {
        setCredentials(certs.join(', '));
      } else if (response.data.cpa_license_number) {
        setCredentials('CPA');
      }
      
      // Use full name from profile if available
      if (response.data.full_name) {
        setAccountantName(response.data.full_name);
        setTypedName(response.data.full_name);
      }
    } catch (error: any) {
      console.log('Could not load full profile, using basic info');
      // This is okay - profile might not be complete yet
    }
  };

  // Initialize signature pad when modal opens
  useEffect(() => {
    if (showSignModal && canvasRef.current && !signaturePadRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 0.5,
        maxWidth: 2.5
      });
    }

    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
      }
    };
  }, [showSignModal]);

  const fetchSignatures = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/signatures`,
        { headers: getAuthHeader() }
      );
      setSignatures(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch signatures:', err);
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const generateTypedSignature = (): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = 'black';
      ctx.font = '48px Brush Script MT, cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
    }
    
    return canvas.toDataURL('image/png');
  };

  const handleSign = async () => {
    try {
      setSigning(true);
      
      let signatureDataUrl: string;

      if (activeTab === 'draw') {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
          setValidationError('Please draw your signature first');
          setSigning(false);
          return;
        }
        signatureDataUrl = signaturePadRef.current.toDataURL('image/png');
      } else {
        if (!typedName.trim()) {
          setValidationError('Please enter your name');
          setSigning(false);
          return;
        }
        signatureDataUrl = generateTypedSignature();
      }

      // Sign the report
      await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/signatures`,
        {
          signature_data_url: signatureDataUrl,
          notes: notes || null
        },
        { headers: getAuthHeader() }
      );

      setSuccessMessage('Report signed successfully!');
      setShowSignModal(false);
      fetchSignatures();
      
      // Reset form
      setNotes('');
      setTypedName('');
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    } catch (err: any) {
      console.error('Failed to sign report:', err);
      setValidationError(err.response?.data?.detail || 'Failed to sign report');
    } finally {
      setSigning(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <p className="text-gray-500">Loading signatures...</p>
      </div>
    );
  }

  // Check if already signed
  const isSigned = signatures && signatures.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6">
        {successMessage && (
          <p className="text-green-600 text-sm mb-4">{successMessage}</p>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileSignature className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">Digital Signature</h3>
          </div>
          {isSigned && (
            <span className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
              <Check className="w-4 h-4" />
              Signed
            </span>
          )}
        </div>

        {/* Show existing signatures */}
        {isSigned ? (
          <div className="space-y-4">
            {signatures.map((sig) => (
              <div key={sig.id} className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <img
                      src={sig.signature_data_url}
                      alt="Signature"
                      className="border-2 border-gray-300 rounded-lg p-2 bg-white max-h-24"
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-semibold">Signed by:</span> {sig.accountant_name}
                    </p>
                    {credentials && (
                      <p className="text-sm">
                        <span className="font-semibold">Credentials:</span> {credentials}
                      </p>
                    )}
                    <p className="text-sm">
                      <span className="font-semibold">Date:</span> {formatDate(sig.signed_at)}
                    </p>
                    {sig.notes && (
                      <p className="text-sm">
                        <span className="font-semibold">Notes:</span> {sig.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg p-6">
            <p className="text-amber-800 mb-4">
              This report has not been digitally signed yet.
            </p>
            {canSign && (
              <button
                onClick={() => setShowSignModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition"
              >
                <FileSignature className="w-5 h-5" />
                Sign Report
              </button>
            )}
          </div>
        )}
      </div>

      {/* Signature Modal */}
      {showSignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <FileSignature className="w-6 h-6" />
                  Sign Report
                </h2>
                <button
                  onClick={() => setShowSignModal(false)}
                  className="text-white hover:text-gray-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-blue-100 mt-2 text-sm">
                Digitally sign this forensic accounting report
              </p>
            </div>

            <div className="p-6">
              {validationError && (
                <p className="text-red-500 text-sm mb-4">{validationError}</p>
              )}

              {/* Tabs */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => { setActiveTab('draw'); setValidationError(null); }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                    activeTab === 'draw'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  ✏️ Draw Signature
                </button>
                <button
                  onClick={() => { setActiveTab('type'); setValidationError(null); }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                    activeTab === 'type'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  ⌨️ Type Signature
                </button>
              </div>

              {/* Draw Signature Tab */}
              {activeTab === 'draw' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Draw your signature below:
                  </label>
                  <div className="border-2 border-gray-300 rounded-lg mb-4 bg-white">
                    <canvas
                      ref={canvasRef}
                      width={600}
                      height={200}
                      className="w-full rounded-lg touch-none"
                    />
                  </div>
                  <button
                    onClick={handleClear}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    🔄 Clear Signature
                  </button>
                </div>
              )}

              {/* Type Signature Tab */}
              {activeTab === 'type' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type your full name:
                  </label>
                  <input
                    type="text"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="mt-4 p-4 border-2 border-gray-300 rounded-lg bg-white h-32 flex items-center justify-center">
                    <p 
                      className="text-5xl text-gray-800"
                      style={{ fontFamily: 'Brush Script MT, cursive' }}
                    >
                      {typedName || 'Your Name Here'}
                    </p>
                  </div>
                </div>
              )}

              {/* Profile Information Display */}
              {profileData && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">
                    📋 Your Profile Information
                  </h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p><strong>Name:</strong> {accountantName || profileData.full_name}</p>
                    {credentials && <p><strong>Credentials:</strong> {credentials}</p>}
                    {profileData.cpa_license_number && (
                      <p><strong>CPA License:</strong> {profileData.cpa_license_number} ({profileData.license_state})</p>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Professional Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Credentials {profileData && credentials ? '(from your profile)' : ''}
                  </label>
                  <input
                    type="text"
                    value={credentials}
                    onChange={(e) => setCredentials(e.target.value)}
                    placeholder="e.g., CPA, CFE, CFF"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {profileData && !credentials && (
                    <p className="text-xs text-amber-600 mt-1">
                      💡 Add certifications to your profile to auto-fill this field
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attestation Statement
                  </label>
                  <textarea
                    value={attestation}
                    onChange={(e) => setAttestation(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Add any notes or comments..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Legal Notice */}
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Legal Notice:</strong> By signing this report, you certify that 
                  the information contained herein is accurate and complete to the best of your 
                  professional knowledge. This signature is legally binding.
                </p>
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleSign}
                  disabled={signing}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                >
                  {signing ? (
                    'Signing...'
                  ) : (
                    <>
                      <FileSignature className="w-5 h-5" />
                      Sign Report
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowSignModal(false)}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
